import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix Leaflet's default icon paths (Webpack 5 + asset hashing breaks them).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const trackPath = (gpsData) =>
  gpsData.filter((p) => typeof p.lat === 'number' && typeof p.lng === 'number')
         .map((p) => [p.lat, p.lng]);

function FitToTrack({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [24, 24] });
  }, [points, map]);
  return null;
}

/**
 * Renders the GPS track of a workout. `scrubIndex` (0..gpsData.length-1)
 * highlights the current position with an orange circle. Start = green flag,
 * Finish = checkered black flag (rendered as larger black circle with white ring).
 */
export default function SessionMap({ gpsData, scrubIndex }) {
  const ref = useRef(null);
  const points = useMemo(() => trackPath(gpsData || []), [gpsData]);

  if (!points.length) {
    return (
      <div
        className="w-full h-64 rounded-xl bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-sm text-zinc-500"
        data-testid="session-map-empty"
      >
        No GPS track captured for this session.
      </div>
    );
  }

  const start = points[0];
  const finish = points[points.length - 1];
  const current = points[Math.min(Math.max(scrubIndex || 0, 0), points.length - 1)];

  return (
    <div
      className="w-full h-64 sm:h-80 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700"
      data-testid="session-map"
    >
      <MapContainer
        ref={ref}
        center={current}
        zoom={15}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitToTrack points={points} />
        <Polyline positions={points} pathOptions={{ color: '#fafafa', weight: 4, opacity: 0.95 }} />
        <Polyline positions={points} pathOptions={{ color: '#10b981', weight: 2 }} />
        <CircleMarker
          center={start}
          radius={7}
          pathOptions={{ color: '#000', weight: 2, fillColor: '#fff', fillOpacity: 1 }}
        />
        <CircleMarker
          center={finish}
          radius={7}
          pathOptions={{ color: '#000', weight: 2, fillColor: '#000', fillOpacity: 1 }}
        />
        <CircleMarker
          center={current}
          radius={8}
          pathOptions={{ color: '#fff', weight: 2, fillColor: '#f97316', fillOpacity: 1 }}
        />
      </MapContainer>
    </div>
  );
}
